import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

const SERVICE_TYPES = ['Pickup', 'Delivery', 'Full-service (on-site setup & sternos)'];

const todayStr = () => new Date().toISOString().slice(0, 10);

/**
 * Catering quote request form. Writes to Firestore `cateringQuotes`
 * (public create; staff read — see firestore.rules). Falls back to a phone
 * prompt if the write fails.
 */
export default function QuoteForm() {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', date: '', guests: '',
    service: SERVICE_TYPES[0], dishes: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errMsg, setErrMsg] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrMsg('');
    try {
      await addDoc(collection(db, 'cateringQuotes'), {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        eventDate: form.date,
        guests: Number(form.guests) || form.guests,
        serviceType: form.service,
        requestedDishes: form.dishes.trim(),
        status: 'new',
        source: 'catering-page',
        submittedAt: serverTimestamp(),
      });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrMsg('Sorry — we couldn’t submit your request. Please call (479) 412-9916.');
    }
  };

  return (
    <section className="cq" id="catering-quote">
      <style>{`
        .cq { padding: 56px 20px; background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%); }
        .cq__inner { max-width: 640px; margin: 0 auto; }
        .cq__label { display: block; text-align: center; color: #d97706; font-weight: 800;
          letter-spacing: .08em; text-transform: uppercase; font-size: 13px; margin-bottom: 6px; }
        .cq__heading { text-align: center; font-size: clamp(26px, 5vw, 38px); font-weight: 800;
          color: #1c1917; margin: 0 0 8px; }
        .cq__sub { text-align: center; color: #57534e; margin: 0 0 28px; }
        .cq__card { background: #fff; border: 1px solid #f1e4d3; border-radius: 20px;
          padding: 26px; box-shadow: 0 16px 40px rgba(120,53,15,0.08); }
        .cq__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .cq__field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
        .cq__field--full { grid-column: 1 / -1; }
        .cq__field label { font-size: 13px; font-weight: 700; color: #44403c; }
        .cq__field .req { color: #dc2626; }
        .cq__field input, .cq__field select, .cq__field textarea {
          font: inherit; width: 100%; box-sizing: border-box; padding: 11px 13px;
          border: 1.5px solid #e7e0d6; border-radius: 11px;
          background: #fdfbf8; color: #1c1917; outline: none; transition: border-color .12s ease; }
        .cq__field input:focus, .cq__field select:focus, .cq__field textarea:focus {
          border-color: #f59e0b; }
        .cq__field textarea { resize: vertical; min-height: 80px; }
        .cq__btn { grid-column: 1 / -1; margin-top: 4px; padding: 14px; border: none; cursor: pointer;
          background: #f59e0b; color: #1a1206; font-weight: 800; font-size: 16px; border-radius: 999px;
          box-shadow: 0 6px 18px rgba(245,158,11,0.4); transition: transform .12s ease; }
        .cq__btn:hover:not(:disabled) { transform: translateY(-1px); }
        .cq__btn:disabled { opacity: .6; cursor: default; }
        .cq__err { grid-column: 1 / -1; color: #b91c1c; font-size: 14px; text-align: center; }
        .cq__success { text-align: center; padding: 20px; }
        .cq__success h3 { font-size: 24px; color: #15803d; margin: 0 0 8px; }
        .cq__success p { color: #57534e; margin: 0; }
        @media (max-width: 520px) { .cq__grid { grid-template-columns: 1fr; } }
      `}</style>
      <div className="cq__inner">
        <span className="cq__label">Request a Quote</span>
        <h2 className="cq__heading">Tell Us About Your Event</h2>
        <p className="cq__sub">Send the details below and we’ll get back to you with a custom quote.</p>

        <div className="cq__card">
          {status === 'sent' ? (
            <div className="cq__success" role="status">
              <h3>🎉 Request received!</h3>
              <p>Thanks, {form.name || 'friend'} — we’ll reach out shortly to plan your event. For anything urgent, call (479) 412-9916.</p>
            </div>
          ) : (
            <form className="cq__grid" onSubmit={submit}>
              <div className="cq__field">
                <label htmlFor="cq-name">Name <span className="req">*</span></label>
                <input id="cq-name" type="text" required value={form.name} onChange={set('name')} autoComplete="name" />
              </div>
              <div className="cq__field">
                <label htmlFor="cq-phone">Phone <span className="req">*</span></label>
                <input id="cq-phone" type="tel" required value={form.phone} onChange={set('phone')} autoComplete="tel" />
              </div>
              <div className="cq__field">
                <label htmlFor="cq-email">Email</label>
                <input id="cq-email" type="email" value={form.email} onChange={set('email')} autoComplete="email" />
              </div>
              <div className="cq__field">
                <label htmlFor="cq-date">Event date <span className="req">*</span></label>
                <input id="cq-date" type="date" required min={todayStr()} value={form.date} onChange={set('date')} />
              </div>
              <div className="cq__field">
                <label htmlFor="cq-guests">Guest count <span className="req">*</span></label>
                <input id="cq-guests" type="number" required min="1" value={form.guests} onChange={set('guests')} placeholder="e.g. 50" />
              </div>
              <div className="cq__field">
                <label htmlFor="cq-service">Service type <span className="req">*</span></label>
                <select id="cq-service" value={form.service} onChange={set('service')}>
                  {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="cq__field cq__field--full">
                <label htmlFor="cq-dishes">Requested dishes / notes</label>
                <textarea id="cq-dishes" value={form.dishes} onChange={set('dishes')}
                  placeholder="e.g. Goat biryani (full tray), paneer butter masala, gulab jamun — plus any dietary needs" />
              </div>
              {status === 'error' && <div className="cq__err">{errMsg}</div>}
              <button className="cq__btn" type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Request Quote'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
